import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { TruckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { cn } from '../utils';
import CompanyBranding from '../components/CompanyBranding';
import LanguageSelector from '../components/LanguageSelector';

const Register = () => {
  const { register, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'mechanic',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);

    // Validation
    const validationErrors = [];
    if (formData.password !== formData.confirmPassword) {
      validationErrors.push(t('auth.validation.passwordsNotMatch'));
    }
    if (formData.password.length < 6) {
      validationErrors.push(t('auth.validation.passwordTooShort'));
    }
    if (!formData.name.trim()) {
      validationErrors.push(t('auth.validation.nameRequired'));
    }
    if (!formData.email.trim()) {
      validationErrors.push(t('auth.validation.emailRequired'));
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      await register(
        formData.name,
        formData.email,
        formData.password,
        formData.role,
        formData.phone
      );
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Error is handled by the auth context
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <CompanyBranding size="large" linkTo={null} />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {t('auth.register.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-dark-300">
            {t('auth.register.hasAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-primary-500 hover:text-primary-400"
            >
              {t('auth.register.signIn')}
            </Link>
          </p>
        </div>

        {errors.length > 0 && (
          <div className="bg-red-900 border border-red-700 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-300">
                  {t('auth.validation.fixErrors', { defaultValue: 'Please fix the following errors:' })}
                </h3>
                <div className="mt-2 text-sm text-red-300">
                  <ul className="list-disc list-inside space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="form-label">
                {t('auth.register.name')}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                className="form-input"
                placeholder={t('auth.register.namePlaceholder', { defaultValue: 'Enter your full name' })}
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="email" className="form-label">
                {t('auth.register.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="form-input"
                placeholder={t('auth.register.emailPlaceholder', { defaultValue: 'Enter your email' })}
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="phone" className="form-label">
                {t('auth.register.phone', { defaultValue: 'Phone Number (Optional)' })}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                className="form-input"
                placeholder={t('auth.register.phonePlaceholder', { defaultValue: 'Enter your phone number' })}
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="role" className="form-label">
                {t('auth.register.role')}
              </label>
              <select
                id="role"
                name="role"
                className="form-input"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="mechanic">{t('auth.roles.mechanic')}</option>
                <option value="admin">{t('auth.roles.admin')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                {t('auth.register.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="form-input pr-10"
                  placeholder={t('auth.register.passwordPlaceholder', { defaultValue: 'Enter your password' })}
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-dark-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-dark-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="form-label">
                {t('auth.register.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="form-input pr-10"
                  placeholder={t('auth.register.confirmPasswordPlaceholder', { defaultValue: 'Confirm your password' })}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-dark-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-dark-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || loading}
              className={cn(
                'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white',
                'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isLoading ? (
                <>
                  <div className="spinner mr-2"></div>
                  {t('auth.register.creating')}
                </>
              ) : (
                t('auth.register.createAccount')
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-dark-400">
              {t('auth.register.agreementText', { defaultValue: 'By creating an account, you agree to our' })}{' '}
              <Link to="/terms" className="text-primary-500 hover:text-primary-400">
                {t('auth.register.termsOfService', { defaultValue: 'Terms of Service' })}
              </Link>{' '}
              {t('auth.register.and', { defaultValue: 'and' })}{' '}
              <Link to="/privacy" className="text-primary-500 hover:text-primary-400">
                {t('auth.register.privacyPolicy', { defaultValue: 'Privacy Policy' })}
              </Link>
            </p>
          </div>
        </form>
        
        {/* Language Selector */}
        <div className="mt-6">
          <LanguageSelector variant="dropdown" />
        </div>
      </div>
    </div>
  );
};

export default Register;